import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Alert,
  Grid,
} from '@mui/material';
import api from '../../api/client';

// ─── Types ───────────────────────────────────────────────────────────────────

interface CRMStatus {
  provider: 'hubspot' | 'salesforce';
  portal_id?: string;
  instance_url?: string;
  connected_at: string;
  last_sync_at?: string;
  sync_enabled: boolean;
  settings: Record<string, any>;
}

interface SyncLogEntry {
  id: string;
  provider: string;
  operation: string;
  external_id?: string;
  call_sid?: string;
  success: boolean;
  error?: string;
  created_at: string;
}

// ─── Outcome stage mapping options ───────────────────────────────────────────

const OUTCOMES = ['completed', 'no-answer', 'busy', 'failed', 'interested', 'callback_requested']; // eslint-disable-line @typescript-eslint/no-unused-vars

const HUBSPOT_STAGES = ['Qualified', 'Meeting Scheduled', 'Proposal Sent', 'Closed Won', 'Closed Lost'];
const SF_STATUSES = ['Open - Not Contacted', 'Working - Contacted', 'Closed - Converted', 'Closed - Not Converted'];

// ─── CRM Card ────────────────────────────────────────────────────────────────

interface CRMCardProps {
  provider: 'hubspot' | 'salesforce';
  integration?: CRMStatus | undefined;
  onConnect: () => void;
  onDisconnect: () => void;
  onTest: () => void;
  onSettingChange: (sync_enabled: boolean, settings: Record<string, any>) => void;
  testResult?: string | undefined;
  loading?: boolean | undefined;
}

const CRMCard: React.FC<CRMCardProps> = ({
  provider,
  integration,
  onConnect,
  onDisconnect,
  onTest,
  onSettingChange,
  testResult,
  loading,
}) => {
  const isHubSpot = provider === 'hubspot';
  const connected = !!integration;

  const handleSyncToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!integration) return;
    onSettingChange(e.target.checked, integration.settings ?? {});
  };

  const handleOutcomeMapping = (outcome: string, value: string) => {
    if (!integration) return;
    const settings = { ...(integration.settings ?? {}), outcomeMapping: { ...(integration.settings?.outcomeMapping ?? {}), [outcome]: value } };
    onSettingChange(integration.sync_enabled, settings);
  };

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography
            variant="h6"
            fontWeight={700}
            color={isHubSpot ? '#ff7a59' : '#00a1e0'}
          >
            {isHubSpot ? '🟠 HubSpot' : '☁️ Salesforce'}
          </Typography>
          <Chip
            size="small"
            label={connected ? 'Connected' : 'Not connected'}
            color={connected ? 'success' : 'default'}
          />
        </Stack>

        {/* Connected info */}
        {connected && integration && (
          <Box mb={2}>
            {integration.portal_id && (
              <Typography variant="body2" color="text.secondary">Portal ID: {integration.portal_id}</Typography>
            )}
            {integration.instance_url && (
              <Typography variant="body2" color="text.secondary" noWrap>Instance: {integration.instance_url}</Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Connected: {new Date(integration.connected_at).toLocaleDateString()}
            </Typography>
            {integration.last_sync_at && (
              <Typography variant="body2" color="text.secondary">
                Last sync: {new Date(integration.last_sync_at).toLocaleString()}
              </Typography>
            )}
          </Box>
        )}

        {/* Actions */}
        <Stack direction="row" spacing={1} mb={2}>
          {!connected ? (
            <Button size="small" variant="contained" onClick={onConnect} disabled={loading ?? false}>
              Connect {isHubSpot ? 'HubSpot' : 'Salesforce'}
            </Button>
          ) : (
            <>
              <Button size="small" variant="outlined" color="error" onClick={onDisconnect} disabled={loading ?? false}>
                Disconnect
              </Button>
              <Button size="small" variant="outlined" onClick={onTest} disabled={loading ?? false}>
                Test
              </Button>
            </>
          )}
        </Stack>

        {testResult && (
          <Alert severity="info" sx={{ mb: 2, py: 0 }}>{testResult}</Alert>
        )}

        {/* Settings (only when connected) */}
        {connected && integration && (
          <>
            <Divider sx={{ my: 1 }} />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={integration.sync_enabled}
                  onChange={handleSyncToggle}
                />
              }
              label="Auto-sync on call completion"
            />

            <Typography variant="caption" color="text.secondary" display="block" mt={1} mb={0.5}>
              Outcome → {isHubSpot ? 'Deal Stage' : 'Lead Status'} mapping
            </Typography>

            {['interested', 'callback_requested'].map((outcome) => (
              <Stack key={outcome} direction="row" alignItems="center" spacing={1} mb={0.5}>
                <Typography variant="caption" sx={{ width: 130 }}>{outcome}</Typography>
                <Select
                  size="small"
                  sx={{ fontSize: 12, height: 28, flex: 1 }}
                  value={integration.settings?.outcomeMapping?.[outcome] ?? ''}
                  onChange={(e) => handleOutcomeMapping(outcome, e.target.value)}
                  displayEmpty
                >
                  <MenuItem value=""><em>— none —</em></MenuItem>
                  {(isHubSpot ? HUBSPOT_STAGES : SF_STATUSES).map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </Stack>
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const CRMIntegrations: React.FC = () => {
  const [integrations, setIntegrations] = useState<CRMStatus[]>([]);
  const [syncHistory, setSyncHistory] = useState<SyncLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    try {
      const [statusRes, historyRes] = await Promise.all([
        api.get('/api/v1/integrations/crm/status'),
        api.get('/api/v1/integrations/crm/sync/history'),
      ]);
      setIntegrations(statusRes.data.integrations ?? []);
      setSyncHistory((historyRes.data.history ?? []).slice(0, 20));
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const getIntegration = (provider: string) => integrations.find((i) => i.provider === provider);

  const handleConnect = async (provider: 'hubspot' | 'salesforce') => {
    setLoading(true);
    try {
      const res = await api.post(`/api/v1/integrations/crm/${provider}/connect`);
      if (res.data.authUrl) {
        window.location.href = res.data.authUrl;
      }
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async (provider: 'hubspot' | 'salesforce') => {
    setLoading(true);
    try {
      await api.post(`/api/v1/integrations/crm/${provider}/disconnect`);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async (provider: 'hubspot' | 'salesforce') => {
    setLoading(true);
    try {
      const res = await api.post(`/api/v1/integrations/crm/${provider}/test`);
      const count = res.data.contactCount ?? res.data.leadCount ?? '?';
      setTestResults((prev) => ({ ...prev, [provider]: `✅ Connected — ${count} ${provider === 'hubspot' ? 'contacts' : 'leads'} found` }));
    } catch (err: any) {
      setTestResults((prev) => ({ ...prev, [provider]: `❌ ${err?.response?.data?.error ?? err.message}` }));
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = async (provider: 'hubspot' | 'salesforce', syncEnabled: boolean, settings: Record<string, any>) => {
    try {
      await api.patch('/api/v1/integrations/crm/settings', { sync_enabled: syncEnabled, settings });
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err.message);
    }
  };

  const handleSyncAllCampaigns = async () => {
    setSyncLoading(true);
    try {
      // Fetch all campaigns and sync each
      const campaignsRes = await api.get('/api/v1/campaigns');
      const campaigns = campaignsRes.data.campaigns ?? [];
      for (const c of campaigns) {
        await api.post(`/api/v1/integrations/crm/sync/campaign/${c.id}`);
      }
      await load();
      alert(`Synced ${campaigns.length} campaigns to CRM.`);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err.message);
    } finally {
      setSyncLoading(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>CRM Integrations</Typography>
          <Typography variant="body2" color="text.secondary">
            Automatically sync call outcomes, contacts, and campaign results to your CRM.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={handleSyncAllCampaigns}
          disabled={syncLoading || integrations.length === 0}
          startIcon={syncLoading ? <CircularProgress size={16} /> : undefined}
        >
          Sync all campaigns
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* CRM cards */}
      <Grid container spacing={2} mb={4}>
        <Grid item xs={12} md={6}>
          <CRMCard
            provider="hubspot"
            integration={getIntegration('hubspot')}
            onConnect={() => handleConnect('hubspot')}
            onDisconnect={() => handleDisconnect('hubspot')}
            onTest={() => handleTest('hubspot')}
            onSettingChange={(e, s) => handleSettingChange('hubspot', e, s)}
            testResult={testResults['hubspot']}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CRMCard
            provider="salesforce"
            integration={getIntegration('salesforce')}
            onConnect={() => handleConnect('salesforce')}
            onDisconnect={() => handleDisconnect('salesforce')}
            onTest={() => handleTest('salesforce')}
            onSettingChange={(e, s) => handleSettingChange('salesforce', e, s)}
            testResult={testResults['salesforce']}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Sync history */}
      <Typography variant="h6" fontWeight={600} mb={1}>Sync History</Typography>
      {syncHistory.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No sync events yet.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Provider</TableCell>
                <TableCell>Operation</TableCell>
                <TableCell>Record</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {syncHistory.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{row.provider}</TableCell>
                  <TableCell>{row.operation}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.external_id ?? row.call_sid ?? '-'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.success ? 'OK' : 'Error'}
                      color={row.success ? 'success' : 'error'}
                    />
                    {row.error && (
                      <Typography variant="caption" color="error" display="block">{row.error}</Typography>
                    )}
                  </TableCell>
                  <TableCell>{new Date(row.created_at).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default CRMIntegrations;
